/**
 * One row on `/messages/requests` (R4). Presentation only: takes props, makes
 * no API call, owns no state. The whole row except the two action buttons
 * opens the preview (R5); the actions never bubble into that (each stops its
 * own click event before it reaches the wrapping div, mirroring
 * `InviteCard.tsx`'s identical HTML-nesting reasoning).
 */
import { Button, Card } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { avatarPlateStyle } from "../profile/avatarPlateStyle";
import {
  formatRequestTimestamp,
  resolveSharedPoolContextLine,
  truncateOneLine,
} from "./requestsFormatting";

export type typeRequestRowProps = {
  fromDisplayName: string;
  fromAvatarUrl: string | null;
  sharedPoolName: string | null;
  firstMessagePreview: string;
  requestedAtIso: string;
  onPressBody: () => void;
  onApprove: () => void;
  onIgnore: () => void;
  isApproving: boolean;
  isIgnoring: boolean;
};

const AvatarPlate = (props: {
  fromAvatarUrl: string | null;
  fromDisplayName: string;
}): ReactNode => {
  if (props.fromAvatarUrl !== null) {
    return (
      <img
        src={props.fromAvatarUrl}
        alt=""
        className="size-10 shrink-0 rounded-full object-cover"
      />
    );
  }
  const initial = props.fromDisplayName.slice(0, 1).toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="grid size-10 shrink-0 place-items-center rounded-full text-body-strong"
      style={avatarPlateStyle(props.fromDisplayName)}
    >
      {initial}
    </span>
  );
};

export const RequestRow = (props: typeRequestRowProps): ReactNode => {
  const sharedPoolLine = resolveSharedPoolContextLine(props.sharedPoolName);

  return (
    <div
      className="cursor-pointer"
      data-testid="request-row"
      aria-label={props.fromDisplayName}
      onClick={() => {
        props.onPressBody();
      }}
    >
      <Card padding="default" tone="default">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <AvatarPlate
              fromAvatarUrl={props.fromAvatarUrl}
              fromDisplayName={props.fromDisplayName}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-body-strong text-ink">
                {props.fromDisplayName}
              </p>
              {sharedPoolLine !== null ? (
                <p className="truncate text-caption text-ink-secondary">
                  {sharedPoolLine}
                </p>
              ) : null}
            </div>
            <span className="shrink-0 text-caption text-ink-secondary">
              {formatRequestTimestamp(props.requestedAtIso)}
            </span>
          </div>
          <p className="truncate text-body text-ink-secondary">
            {truncateOneLine(props.firstMessagePreview)}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              className="min-w-32 flex-1"
              loading={props.isApproving}
              loadingLabel="Approving"
              disabled={props.isApproving || props.isIgnoring}
              onClick={(event) => {
                event.stopPropagation();
                props.onApprove();
              }}
            >
              Approve
            </Button>
            <Button
              variant="outline"
              className="min-w-32 flex-1"
              loading={props.isIgnoring}
              loadingLabel="Ignoring"
              disabled={props.isApproving || props.isIgnoring}
              onClick={(event) => {
                event.stopPropagation();
                props.onIgnore();
              }}
            >
              Ignore
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
